import { UserProfile } from 'fitbeat.gym-core';
import { BaseUserProfile, IHeartRate } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import {
    FlatList,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { formValueSelector, reduxForm } from 'redux-form';
import { Button, ButtonTypes } from '../../components';
import Theme, { ThemeType } from '../../theme';
import { ChevronRightIcon } from '../../ui/components';
import { ConnectingLoaderIcon } from '../../ui/components/Icons';
import { defaultScale } from '../../utils/scaling';
import {
    setTestingStub,
    startScanningForHeartRateDevices,
    UpdateHeartRateMonitorsFn,
    updateUserHomeHRM,
} from '../heartRateActions';
import { IAppStore, Orientation } from '../reducer';
import {
  heartRateDeviceListSelector, heartRateDiscoverDidTimeoutSelector, heartRateDiscoverErrorSelector,
  isSavingHeartRateIdentifierSelector,
  latestConnectedAppHRSelector,
} from '../selectors/heartRateMonitorSelectors';
import {
    orientationSelector,
} from '../selectors/homeWorkoutSelectors';
import CurrentConnectedAppHeartRateBanner from './CurrentConnectedAppHeartRateBanner';
import { themeStyleMapper } from './HeartRateMonitorSetup';
import { HomeWorkoutResponsiveLayout } from './HomeWorkoutResponsiveLayout';

interface IProps {
    themeType: ThemeType;
    userProfile: UserProfile;
    heartRateDeviceList: any[];
    heartRateDiscoverDidTimeout: boolean;
    heartRateDiscoverError: string | null;
    orientation: Orientation;
    latestConnectedAppHR: IHeartRate | null;
    isSavingHeartRateIdentifier: boolean;
    updateUserHeartRateMonitors: UpdateHeartRateMonitorsFn;
    userHasConnectedAppForHeartRate: boolean;

    t: (key: string, keys?: any) => string;
    userUpdateFunc(): any;
    startScanForDevices(): void;
    useTestingStub(flag: boolean): void;
    saveHeartRateMonitorIdentifier(identifier: string): void;
    showFaq(): void;
}

const connectingSpinnerSize = defaultScale(50);

class CaptureHeartRateDeviceNumberComponent
    extends React.PureComponent<IProps, any>  {

    constructor(props: any) {
        super(props);

        const { initialize, userProfile } = props;
        initialize({ heartRateMonitorIdentifier: userProfile.heartRateMonitors?.home });
    }

    public componentDidMount = async () => {
        this.props.startScanForDevices();
    }

    public useTestingStubAndStartScanForDevices = () => {
        const { useTestingStub, startScanForDevices } = this.props;
        useTestingStub(true);
        startScanForDevices();
    }

    public saveHeartRateMonitorAndGoToStep = async (heartRateMonitorIdentifier: string) => {
        const { saveHeartRateMonitorIdentifier } = this.props;
        saveHeartRateMonitorIdentifier(heartRateMonitorIdentifier);
    }

    public render() {
        const { t, startScanForDevices, showFaq,
            heartRateDeviceList, heartRateDiscoverError, userProfile,
            isSavingHeartRateIdentifier,
            latestConnectedAppHR,
            themeType = ThemeType.light, orientation,
            userHasConnectedAppForHeartRate,
        } = this.props;
        const themeStyle = themeStyleMapper[themeType];

        const isPortrait = orientation === 'PORTRAIT';
        const responsiveFlexContainer = isPortrait
            ? styles.flexPortrait
            : styles.flexLandscape;

        const DeviceRow = ({ device, onPress }) => {
            return (
                <TouchableOpacity onPress={onPress} disabled={isSavingHeartRateIdentifier}>
                    <View style={styles.deviceRowContainer}>
                        <View>
                            <Text style={[styles.title, themeStyle.textColor1]}>{device.name || 'Unknown Device'}</Text>
                            {
                                device.name !== device.localName &&
                                <Text style={[styles.subTitle, themeStyle.textColor1]}>{device.maskedLocalName}</Text>
                            }
                            {BaseUserProfile.isFeatureEnabledForUser('alpha', this.props.userProfile) &&
                                <View style={styles.deviceInfo}>
                                    <Text style={[styles.subTitle, themeStyle.textColor1]}>id: {device.id}</Text>
                                    <Text style={[styles.subTitle, themeStyle.textColor1]}>
                                        manufacturerData: {device.manufacturerData}
                                    </Text>
                                </View>
                            }
                        </View>
                        <View style={styles.connectBtnContainer}>
                            {
                                isSavingHeartRateIdentifier ? <Text style={styles.connect}>{t('loading')}</Text> :
                                <>
                                    <Text style={[styles.connect, { color: Theme.PRIMARY }]}>{t('connect')}</Text>
                                    <ChevronRightIcon width={18} height={18} color={Theme.PRIMARY} />
                                </>
                            }
                        </View>
                    </View>
                </TouchableOpacity>
            );
        };

        const DeviceListSeperator = () => (
            <View style={[styles.listSeperator, themeStyle.seperator]}></View>
        );

        const Troubleshooting = () => {
            const troubleshooting = [t('troubleshooting1'), t('troubleshooting2'), t('troubleshooting3')];

            return (
                <View style={[styles.flexGrow, styles.column]}>
                    <Text style={[styles.troubleshootingHeading, themeStyle.textColor1]}>{t('troubleshooting')}</Text>
                    {
                        troubleshooting.map((x) =>
                            <View style={[styles.row, styles.troubleshootingRowMargin]}>
                                <View style={styles.bullet}>
                                    <ChevronRightIcon width={defaultScale(14)} height={defaultScale(14)}
                                        color={Theme.PRIMARY} />
                                </View>
                                <View style={styles.bulletText}>
                                    <Text style={[styles.contentText, themeStyle.contentText]}>{x}</Text>
                                </View>
                            </View>,
                        )
                    }
                </View>
            );
        };

        const Status = () => {
            if (isSavingHeartRateIdentifier) {
                return (
                    <View style={{
                        alignItems: 'center',
                        marginVertical: defaultScale(15),
                    }}>
                        <View>
                            <ConnectingLoaderIcon width={connectingSpinnerSize} height={connectingSpinnerSize}
                                animating={!heartRateDiscoverError}
                                color={themeStyle.iconColor2.color} />
                        </View>
                        <View style={{
                            marginVertical: defaultScale(10),
                        }}>
                            <Text style={[styles.scanDescriptionText, themeStyle.textColor1]}>
                                {t('connecting')}
                            </Text>
                        </View>
                    </View>
                )
            }

            return (
                <View style={styles.connectContainer}>
                    <View style={{marginBottom: defaultScale(5)}}>
                        <ConnectingLoaderIcon width={connectingSpinnerSize} height={connectingSpinnerSize}
                            animating={!heartRateDiscoverError}
                            color={themeStyle.iconColor2.color} />
                    </View>
                    <View>
                        <Text style={[styles.scanDescriptionText, themeStyle.textColor1]}>
                            {heartRateDiscoverError ?
                                t(heartRateDiscoverError) : t('scanForavailableMonitors')}
                        </Text>
                        <Text style={[styles.subText, themeStyle.textColor2]}>
                            {t(userHasConnectedAppForHeartRate
                              ? 'scanningSubTextIfUserHasAppleWatch'
                              : 'makeSureDeviceActivated')}
                        </Text>
                    </View>
                </View>
            )
        }

        return (
            <View style={[styles.container, themeStyle.container]}>
                <HomeWorkoutResponsiveLayout>
                    <View style={styles.containerFlex}>
                        <CurrentConnectedAppHeartRateBanner
                          latestConnectedAppHR={latestConnectedAppHR}
                          t={t}
                          themeType={themeType}
                          showFaq={showFaq}
                        />
                        <ScrollView contentContainerStyle={styles.contentContainerStyle}>
                            <Status />
                            {!isSavingHeartRateIdentifier &&
                                <View style={heartRateDeviceList && heartRateDeviceList.length > 0 &&
                                    [styles.borderTopBottom, themeStyle.borderTopBottom]}>

                                    <FlatList
                                        data={heartRateDeviceList}
                                        ItemSeparatorComponent={DeviceListSeperator}
                                        renderItem={({ item, index }) => (
                                            <DeviceRow device={item}
                                                onPress={() =>
                                                    this.saveHeartRateMonitorAndGoToStep(item.localName || item.id)} />
                                        )}
                                        keyExtractor={(item) => item.compoundKey}
                                    />
                                </View>}

                            {heartRateDiscoverError &&
                                <Button
                                    title={t('tryAgain')}
                                    onPress={() => startScanForDevices()}
                                    fontColor={Theme.PRIMARY}
                                    buttonType={ButtonTypes.TRANSPARENT}
                                    containerStyle={[styles.buttonContainer]}
                                />
                            }
                        </ScrollView>
                    </View>
                    <View style={responsiveFlexContainer}>
                        <Troubleshooting />

                        {/* <FeatureToggle
                            releaseType='alpha'
                            userProfile={userProfile}>
                            <Button
                                title={'Use testing stub'}
                                onPress={() => this.useTestingStubAndStartScanForDevices()}
                                fontColor={Theme.PRIMARY}
                                buttonType={ButtonTypes.TRANSPARENT}
                                containerStyle={[styles.buttonContainer]}
                            />
                        </FeatureToggle> */ }

                        <View style={styles.footer}>
                            <Button
                                title={t('compatibilityAndInstructions')}
                                onPress={showFaq}
                                fontColor={Theme.PRIMARY}
                                buttonType={ButtonTypes.OUTLINE}
                                containerStyle={[styles.buttonContainer]}
                            />
                        </View>
                    </View>
                </HomeWorkoutResponsiveLayout>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
    },
    containerFlex: {
        flex: 1,
    },
    flexLandscape: {
        flex: 1,
    },
    flexPortrait: {
        flex: 0,
    },
    scanningContainer: {
        paddingTop: defaultScale(34),
        paddingBottom: defaultScale(40),
    },
    contentContainerStyle: {
        flexGrow: 1,
        justifyContent: 'center',
    },
    connectContainer: {
        flexWrap: 'wrap',
        flexDirection: 'row',
        justifyContent: 'center',
        marginVertical: defaultScale(15),
    },
    iconContainer: {
        paddingBottom: defaultScale(20),
        alignSelf: 'center',
    },
    buttonFlexContainer: {
        flexDirection: 'row',
        width: '100%',
        justifyContent: 'center',
    },
    activityIndicator: {
        paddingBottom: defaultScale(20),
    },
    scanDescriptionText: {
        ...Theme.textStyle.heading,
        color: Theme.GREY_DARK_MEDIUM,
        marginHorizontal: defaultScale(22),
        marginBottom: defaultScale(4),
        alignSelf: 'center',
        textAlign: 'center',
    },
    subText: {
        ...Theme.textStyle.bodyCopyReg,
        marginVertical: defaultScale(4),
        marginHorizontal: defaultScale(6),
        alignSelf: 'center',
        textAlign: 'center',
    },
    footer: {
        justifyContent: 'flex-end',
    },
    buttonContainer: {
        width: '100%',
    },
    connectBtnContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
    },
    connect: {
        ...Theme.textStyle.bodyCopyBold,
        paddingRight: defaultScale(4),
    },
    title: {
        ...Theme.textStyle.bodyCopyBold,
    },
    subTitle: {
        ...Theme.textStyle.bodyCopyReg,
    },
    borderTopBottom: {
        borderTopWidth: 1,
        borderBottomWidth: 1,
    },
    deviceRowContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: defaultScale(12),
        paddingHorizontal: defaultScale(10),
    },
    listSeperator: {
        height: defaultScale(1),
        width: '100%',
        backgroundColor: Theme.GREY_LIGHT,
    },
    errorDescriptionText: {
        ...Theme.textStyle.heading,
        color: Theme.GREY_DARK_MEDIUM,
        paddingVertical: defaultScale(18),
        marginTop: defaultScale(15),
        marginBottom: defaultScale(4),
        alignSelf: 'center',
        textAlign: 'center',
    },
    column: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    bullet: {
        marginTop: defaultScale(5),
        marginRight: defaultScale(15),
        alignSelf: 'flex-start',
    },
    bulletText: {
        flex: 1,
    },
    contentText: {
        ...Theme.textStyle.bodyCopyReg,
        marginBottom: defaultScale(5),
    },
    troubleshootingRowMargin: {
        marginBottom: defaultScale(5),
    },
    troubleshootingHeading: {
        ...Theme.textStyle.bodyCopyBold,
        marginTop: defaultScale(18),
        marginBottom: defaultScale(12),
    },
    flexGrow: {
        flexGrow: 1,
    },
    deviceInfo: {
        maxWidth: '80%',
    },
});

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps): Partial<IProps> => {
    return {
        startScanForDevices: () => dispatch(startScanningForHeartRateDevices()),
        useTestingStub: (flag) => dispatch(setTestingStub(flag)),
        saveHeartRateMonitorIdentifier: (identifier) => dispatch(
          updateUserHomeHRM(identifier, ownProps.updateUserHeartRateMonitors)),
    };
};
const selector = formValueSelector('serialNumber');
const mapStateToProps = (state: IAppStore) => {
    const heartRateMonitorNumber = selector(state, 'heartRateMonitorIdentifier');
    return {
        heartRateMonitorNumber,
        isSavingHeartRateIdentifier: isSavingHeartRateIdentifierSelector(state),
        heartRateDeviceList: heartRateDeviceListSelector(state),
        heartRateDiscoverDidTimeout: heartRateDiscoverDidTimeoutSelector(state),
        heartRateDiscoverError: heartRateDiscoverErrorSelector(state),
        orientation: orientationSelector(state),
        latestConnectedAppHR: latestConnectedAppHRSelector(state),
        userHasConnectedAppForHeartRate: state.heartRateMonitor.userHasConnectedAppForHeartRate,
    };
};

const CaptureHeartRateDeviceNumberRedux =
    connect(mapStateToProps, mapDispatchToProps)(CaptureHeartRateDeviceNumberComponent);
export const CaptureHeartRateDeviceNumber = reduxForm({ form: 'serialNumber' })(
    translate(['heartRateDevice'], { wait: true })(CaptureHeartRateDeviceNumberRedux));
